import { cx } from "@emotion/css";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { type ReactNode, useEffect, useState } from "react";
import { SwipeableContent } from "src/components/Default/LekUI/SwipableDrawer";
import MuiPopper from "src/components/NewComponents/Default/Popper";
import isMobile from "src/helpers/isMobile";

interface PopperChildProps {
	closePopper: () => void;
}

interface PopperProps {
	children: (props: PopperChildProps) => ReactNode;
	renderTrigger: (props: {
		anchor: HTMLElement | null;
		setAnchor: (e: React.MouseEvent) => void;
	}) => ReactNode;
	className?: string;
	onCancel?: () => void;
	onOpen?: () => void;
}

export const Popper = ({
	children: renderContent,
	renderTrigger,
	className,
	onCancel,
	onOpen,
}: PopperProps) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (anchor) {
			onOpen?.();
		}
	}, [anchor]);

	const closePopper = () => {
		setAnchor(null);
	};

	const handleCancel = () => {
		onCancel?.();
		closePopper();
	};

	const renderPopper = () => {
		if (isMobile()) {
			return (
				<SwipeableContent
					isOpen={!!anchor}
					onClose={handleCancel}
					onOpen={(e) =>
						setAnchor((e as React.SyntheticEvent<HTMLElement>).currentTarget)
					}
				>
					<div className="px-16">{renderContent({ closePopper })}</div>
				</SwipeableContent>
			);
		}

		const PopperComponent = MuiPopper as React.ComponentType<{
			open: boolean;
			anchorEl: HTMLElement | null;
			placement: string;
			className: string;
			children: React.ReactNode;
		}>;

		return (
			<PopperComponent
				open={!!anchor}
				anchorEl={anchor}
				placement="bottom-end"
				className={cx("popper", className)}
			>
				<ClickAwayListener onClickAway={handleCancel}>
					<div>{renderContent({ closePopper })}</div>
				</ClickAwayListener>
			</PopperComponent>
		);
	};

	return (
		<>
			{renderTrigger({
				anchor,
				setAnchor: (e) => setAnchor(e.currentTarget as HTMLElement),
			})}
			{renderPopper()}
		</>
	);
};
